import React, { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import api, { getAccessTokenExpiry } from '../utils/api';
import sessionDB from '../utils/sessionDB';
import { expireSession, SESSION_EXPIRED_EVENT } from '../utils/sessionExpiry';
const WARNING_MS = 5 * 60 * 1000;
export default function SessionExpiryModal() {
  const [mode, setMode] = useState(null);
  const [extending, setExtending] = useState(false);
  const [extensionError, setExtensionError] = useState('');
  const extensionPending = useRef(false);
  const warnedExpiry = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    let timer;
    let disposed = false;
    let processing = false;
    const check = async () => {
      clearTimeout(timer);
      if (disposed || processing) return;
      const session = sessionDB.getSession();
      if (!session.accessToken) {
        warnedExpiry.current = null;
        setMode(current => current === 'warning' ? null : current);
        return;
      }
      const expiry = getAccessTokenExpiry(session);
      if (!expiry) return;
      const remaining = expiry - Date.now();
      const key = session.accessToken + ':' + expiry;
      if (warnedExpiry.current !== key) setMode(null);
      if (remaining <= 0) {
        processing = true;
        try {
          if (session.rememberMe === true && session.refreshToken) {
            try { await api.ensureValidAccessToken(); }
            catch { await expireSession(session.accessToken); }
          } else { await expireSession(session.accessToken); }
        } finally { processing = false; }
        if (!disposed) timer = setTimeout(check, 1000);
        return;
      }
      if (remaining <= WARNING_MS && warnedExpiry.current !== key) {
        warnedExpiry.current = key;
        setMode('warning');
      }
      timer = setTimeout(check, Math.min(remaining > WARNING_MS ? remaining - WARNING_MS : remaining, 2147483647));
    };
    const expired = () => { clearTimeout(timer); setMode('expired'); navigate('/', { replace: true }); };
    window.addEventListener(SESSION_EXPIRED_EVENT, expired);
    window.addEventListener('session-updated', check);
    window.addEventListener('storage', check);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    check();
    return () => {
      disposed = true;
      clearTimeout(timer);
      window.removeEventListener(SESSION_EXPIRED_EVENT, expired);
      window.removeEventListener('session-updated', check);
      window.removeEventListener('storage', check);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [navigate]);
  // Dismissing the warning never changes the session's expiry timer.
  useEffect(() => {
    if (mode !== 'warning' || extending) return undefined;
    const timeout = setTimeout(() => setMode(current => current === 'warning' ? null : current), 30 * 1000);
    return () => clearTimeout(timeout);
  }, [mode, extending]);

  useEffect(() => {
    if (mode !== 'warning') setExtensionError('');
  }, [mode]);

  const extendSession = async () => {
    if (extensionPending.current) return;
    extensionPending.current = true;
    setExtending(true);
    setExtensionError('');
    try {
      await api.refreshAccessToken({ userInitiated: true });
      setMode(current => current === 'warning' ? null : current);
    } catch {
      setExtensionError('We could not extend your session. Please save your work and sign in again before your session expires.');
    } finally {
      extensionPending.current = false;
      setExtending(false);
    }
  };

  const expired = mode === 'expired';
  return (
    <Dialog.Root open={Boolean(mode)} onOpenChange={open => { if (!open && !expired && !extending) setMode(null); }}>
      <Dialog.Portal>
        <Dialog.Overlay style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10000 }} />
        <Dialog.Content onEscapeKeyDown={event => { if (expired || extending) event.preventDefault(); }} onInteractOutside={event => event.preventDefault()}
          style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(440px, calc(100vw - 32px))', padding: 28, borderRadius: 12, background: 'white', color: '#172033', zIndex: 10001, boxShadow: '0 20px 60px #0004' }}>
          <Dialog.Title style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>{expired ? 'Session expired' : 'Your session is expiring soon'}</Dialog.Title>
          <Dialog.Description style={{ fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
            {expired ? 'Your session has expired. For your security, you have been signed out. Please sign in again to continue.' : 'Your session will expire in 5 minutes or less. Select Extend Session to continue, or save any work in progress before your session ends.'}
          </Dialog.Description>
          {!expired && extensionError && <p role="alert" style={{ color: '#b91c1c', marginBottom: 16 }}>{extensionError}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          {!expired && <button type="button" disabled={extending} onClick={extendSession}
            style={{ background: '#1d4ed8', color: 'white', padding: '10px 18px', borderRadius: 6, fontWeight: 600, opacity: extending ? 0.6 : 1 }}>
            {extending ? 'Extending session...' : 'Extend Session'}
          </button>}
          <button type="button" disabled={!expired && extending} style={{ background: '#1d4ed8', color: 'white', padding: '10px 18px', borderRadius: 6, fontWeight: 600 }} onClick={() => setMode(null)}>{expired ? 'Ok' : 'Ok'}</button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}